import { defaultCleaningOptions, siteConfig } from '../config/site';
import { modeLabels, optionLabels, pages, type PageContent, type PageKey } from '../content/pages';
import { escapeHtml } from '../core/escape';
import { formatStats, totalChanges } from '../core/stats';
import { MIN_STYLE_SAMPLE_WORDS } from '../core/text-analysis-config';
import type { AiStyleIndicator } from '../core/text-analysis';
import type { DownloadFormat } from '../exports/download';
import { registerFormatting, registerVisit, type PublicCounters } from '../services/public-counter';
import type { CleaningOptions, InputSnapshot, OutputMode, SourceFormat, TransformResult } from '../types/app';

const EXAMPLE_TEXT = `## Projektupdate\n\n**Ziel:** Ein verständlicher Überblick für das Team.\n\nDer aktuelle Entwurf wurde aus einer PDF\nkopiert und enthält überflüssige  Abstände,\neinen unglücklichen Zeilenum-\nbruch und ein unsichtbares\u200B Zeichen.\n\n- Inhalt fachlich prüfen\n- Rückmeldung bis Freitag senden\n\nMehr unter [kopivaro.de](https://kopivaro.de).`;

const AI_STYLE_EXAMPLE_TEXT = `In der heutigen Zeit gewinnt klare Kommunikation zunehmend an Bedeutung. Es ist wichtig zu beachten, dass verständliche Texte in vielen Bereichen eine zentrale Rolle spielen. Darüber hinaus ermöglicht es eine strukturierte Darstellung, komplexe Inhalte effizient zu vermitteln. Zudem trägt ein ganzheitlicher Ansatz maßgeblich dazu bei, unterschiedliche Zielgruppen nahtlos zu erreichen. Nicht nur die Wortwahl, sondern auch der Aufbau ist dabei von entscheidender Bedeutung.

Gleichzeitig gilt es, Chancen und Herausforderungen ausgewogen zu betrachten. Eine robuste Struktur schafft Orientierung und kann das vorhandene Potenzial optimal entfalten. Abschließend lässt sich festhalten, dass eine sorgfältige Ausarbeitung unerlässlich ist. Zusammenfassend lässt sich sagen, dass klare, dynamische und zukunftsweisende Kommunikation einen wichtigen Beitrag zum Erfolg leisten kann.`;

const sourceOptions: Array<[SourceFormat, string]> = [
  ['auto', 'Automatisch erkennen'],
  ['plain', 'Klartext'],
  ['markdown', 'Markdown'],
  ['html', 'HTML / Rich Text'],
  ['office', 'Word / Office'],
  ['pdf', 'PDF-ähnlicher Text'],
];

const relatedTools: Array<{ key: PageContent['key']; href: string; label: string }> = [
  { key: 'humanize', href: '/ki-text-humanisieren/', label: 'KI-Text humanisieren' },
  { key: 'formatting', href: '/text-formatierung-entfernen/', label: 'Formatierung entfernen' },
  { key: 'chatgpt-word', href: '/chatgpt-text-in-word/', label: 'ChatGPT → Word' },
  { key: 'pdf', href: '/pdf-text-bereinigen/', label: 'PDF-Text bereinigen' },
  { key: 'pdf-export', href: '/text-in-pdf-umwandeln/', label: 'Text → PDF' },
  { key: 'analysis', href: '/ki-text-erkennen/', label: 'KI-Stilmuster prüfen' },
  { key: 'markdown', href: '/markdown-entfernen/', label: 'Markdown entfernen' },
  { key: 'linebreaks', href: '/zeilenumbrueche-entfernen/', label: 'Zeilenumbrüche' },
  { key: 'whatsapp', href: '/text-fuer-whatsapp-formatieren/', label: 'WhatsApp-Text' },
  { key: 'html', href: '/html-bereinigen/', label: 'HTML bereinigen' },
  { key: 'html-markdown', href: '/html-in-markdown-umwandeln/', label: 'HTML → Markdown' },
  { key: 'markdown-html', href: '/markdown-in-html-umwandeln/', label: 'Markdown → HTML' },
];

interface StoredSettings {
  mode?: OutputMode;
  options?: Partial<CleaningOptions>;
  theme?: 'light' | 'dark';
}

function readSettings(): StoredSettings {
  try {
    return JSON.parse(localStorage.getItem('kopivaro-settings') ?? '{}') as StoredSettings;
  } catch {
    return {};
  }
}

function saveSettings(settings: StoredSettings): void {
  localStorage.setItem('kopivaro-settings', JSON.stringify(settings));
}

function adSlot(position: 'introduction' | 'result' | 'guide'): string {
  const configured = siteConfig.ads.enabled && siteConfig.ads.slots[position];
  if (siteConfig.ads.enabled && siteConfig.ads.autoAds && !configured && !import.meta.env.DEV) return '';
  return `<aside class="ad-slot ad-slot--${position}" aria-label="Werbefläche" data-ad-position="${position}">
    <span>${configured ? 'Anzeige' : siteConfig.ads.enabled ? 'Auto-Ads-Vorschau' : 'Werbefläche'}</span>
  </aside>`;
}

function counterTemplate(): string {
  const counters: Array<[keyof PublicCounters, string]> = [
    ['visitorsToday', 'Besucher heute'],
    ['visitorsTotal', 'Besucher gesamt'],
    ['formattingToday', 'Formatierungen heute'],
    ['formattingTotal', 'Formatierungen gesamt'],
  ];
  return `<section class="public-stats" aria-labelledby="public-stats-title">
    <div><p class="eyebrow">Kopivaro in Zahlen</p><h2 id="public-stats-title">Gemeinsam weniger Formatierungschaos</h2><p class="counter-note" id="counter-note">Datensparsam und ohne Speicherung von Texten, IP-Adressen oder Gerätekennungen.</p></div>
    <dl aria-live="polite">${counters.map(([key, label]) => `<div><dt>${label}</dt><dd data-public-counter="${key}">–</dd></div>`).join('')}</dl>
  </section>`;
}

function renderFaq(items: PageContent['faqs']): string {
  return items.map((item) => `<details class="faq-item"><summary>${escapeHtml(item.question)}</summary><p>${escapeHtml(item.answer)}</p></details>`).join('');
}

function renderGuide(page: PageContent): string {
  return `<section class="content-section guide-section" aria-labelledby="guide-title">
    <div class="section-heading"><p class="eyebrow">Ratgeber</p><h2 id="guide-title">${escapeHtml(page.guideTitle)}</h2></div>
    <div class="search-answer"><article><p class="eyebrow">Kurz erklärt</p><h3>${escapeHtml(page.title)}</h3><p>${escapeHtml(page.quickAnswer)}</p></article><aside aria-labelledby="use-cases-title"><h3 id="use-cases-title">Gut geeignet für</h3><ul>${page.useCases.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul></aside></div>
    <ol class="steps">${page.guide.map((step) => `<li><span>${escapeHtml(step)}</span></li>`).join('')}</ol>
    <div class="example-card">
      <div><h3>Vorher</h3><pre>${escapeHtml(page.before)}</pre></div>
      <div><h3>Nachher</h3><pre>${escapeHtml(page.after)}</pre></div>
    </div>
    ${page.key === 'home' ? `<div class="explain-grid">
      <article><h3>Warum Formatierungen kaputtgehen</h3><p>Word, Webseiten, E-Mails und KI-Chats legen beim Kopieren unsichtbare HTML-Elemente, Schriftangaben oder Markdown-Zeichen in die Zwischenablage. Das Zielprogramm interpretiert diese Informationen jeweils anders.</p></article>
      <article><h3>Was lokal bedeutet</h3><p>Die Textverarbeitung findet im Arbeitsspeicher deines Browsers statt. Es gibt keine Anmeldung und keinen Text-Upload. Lokal gespeichert werden nur Einstellungen und zwei nicht identifizierende Zählermarker.</p></article>
      <article><h3>PDFs mit Augenmaß reparieren</h3><p>Kopivaro verbindet nur wahrscheinlich künstliche Zeilenumbrüche. Leerzeilen, Listen und kurze Überschriften bleiben Grenzen. Kontrolliere bei Fachbegriffen, ob eine Silbentrennung wirklich zusammengehört.</p></article>
      <article><h3>KI-Texte ehrlich prüfen</h3><p>Die Herkunft eines fertigen Textes ist nicht zuverlässig beweisbar. Kopivaro zeigt deshalb messbare Textwerte und konkrete Stilmerkmale statt einer erfundenen KI-Wahrscheinlichkeit.</p></article>
    </div>` : ''}
    <nav class="related-tools" aria-labelledby="related-tools-title"><h3 id="related-tools-title">Direkt zum passenden Werkzeug</h3><div>${relatedTools.filter((item) => item.key !== page.key).map((item) => `<a href="${item.href}">${escapeHtml(item.label)}</a>`).join('')}</div></nav>
  </section>`;
}

function toolTemplate(page: PageContent): string {
  const cleanButtonLabel = page.key === 'humanize' ? 'KI-Text jetzt humanisieren' : 'Text jetzt bereinigen';
  const modes = (Object.keys(modeLabels) as OutputMode[]).map((mode) => `<button class="mode-button" type="button" data-mode="${mode}" aria-pressed="false">
    <span>${escapeHtml(modeLabels[mode].title)}</span><small>${escapeHtml(modeLabels[mode].description)}</small>
  </button>`).join('');
  const options = optionLabels.map((option) => `<label class="option-item" title="${escapeHtml(option.help)}">
    <input type="checkbox" data-option="${option.key}">
    <span><strong>${escapeHtml(option.label)}</strong><small>${escapeHtml(option.help)}</small></span>
  </label>`).join('');
  const sources = sourceOptions.map(([value, label]) => `<option value="${value}">${escapeHtml(label)}</option>`).join('');

  return `<section class="tool" id="werkzeug" aria-labelledby="tool-title">
    <div class="tool-heading"><div><p class="eyebrow">Dein Werkzeug</p><h2 id="tool-title">Einfügen, Ziel wählen, fertig</h2></div><p class="local-note"><span aria-hidden="true">●</span> Dein Text wird ausschließlich in deinem Browser verarbeitet.</p></div>
    <div class="mode-grid" role="group" aria-label="Zielformat wählen">${modes}</div>
    <div class="workspace">
      <section class="panel input-panel" aria-labelledby="input-title">
        <div class="panel-heading"><div><span class="step-number">1</span><h3 id="input-title">Text einfügen</h3></div><div class="compact-actions"><button type="button" class="button button--quiet" id="file-button">Datei öffnen</button><button type="button" class="button button--quiet" id="example-button">Beispiel laden</button><button type="button" class="button button--quiet" id="reset-button">Zurücksetzen</button><input class="sr-only" type="file" id="file-input" accept=".pdf,.docx,.html,.htm,.md,.markdown,.txt" aria-label="PDF, DOCX, HTML, Markdown oder TXT lokal öffnen"></div></div>
        <div id="source-editor" class="source-editor" contenteditable="true" role="textbox" aria-multiline="true" aria-label="Zu bereinigenden Text einfügen" data-placeholder="Text hier einfügen oder direkt schreiben …" spellcheck="true"></div>
        <p class="file-note">Oder PDF, DOCX, HTML, Markdown bzw. TXT lokal öffnen – ohne Upload.</p>
        <div class="source-row"><label for="source-format">Quelle</label><select id="source-format">${sources}</select><span class="detection-note" id="detection-note">Noch kein Text erkannt</span></div>
        <button type="button" class="button button--primary button--wide" id="clean-button">${cleanButtonLabel}</button>
        <details class="options"><summary>Bereinigung anpassen <span>13 Optionen</span></summary><div class="options-grid">${options}</div></details>
      </section>
      <section class="panel output-panel" aria-labelledby="output-title">
        <div class="panel-heading"><div><span class="step-number">2</span><h3 id="output-title">Ergebnis prüfen</h3></div><div class="view-toggle" role="group" aria-label="Ansicht wählen"><button type="button" data-view="before" aria-pressed="false">Vorher</button><button type="button" data-view="after" aria-pressed="true">Nachher</button></div></div>
        <div id="preview" class="preview preview--empty" tabindex="0" aria-label="Vorschau"><p>Dein bereinigter Text erscheint hier.</p></div>
        <div class="change-summary" aria-labelledby="changes-title"><h4 id="changes-title">Was wurde geändert?</h4><ul id="change-list"><li>Noch keine Verarbeitung durchgeführt.</li></ul></div>
        <div class="result-actions">
          <button type="button" class="button button--primary" id="copy-button" disabled>Ausgabe kopieren</button>
          <button type="button" class="button button--secondary" id="copy-plain-button" disabled>Klartext kopieren</button>
          <div class="download-control"><label class="sr-only" for="download-format">Downloadformat</label><select id="download-format"><option value="txt">TXT</option><option value="pdf">PDF</option><option value="html">HTML</option><option value="md">Markdown</option></select><button type="button" class="button button--secondary" id="download-button" disabled>Herunterladen</button></div>
        </div>
        <details class="text-analysis" id="text-analysis"${page.key === 'analysis' || page.key === 'humanize' ? ' open' : ''}><summary><span>Textanalyse</span><small id="analysis-summary">Nach der Bereinigung verfügbar</small></summary><div class="analysis-body"><dl class="analysis-metrics"><div><dt>Wörter</dt><dd data-analysis="words">–</dd></div><div><dt>Zeichen</dt><dd data-analysis="characters">–</dd></div><div><dt>Sätze</dt><dd data-analysis="sentences">–</dd></div><div><dt>Absätze</dt><dd data-analysis="paragraphs">–</dd></div><div><dt>Lesezeit</dt><dd data-analysis="readingMinutes">–</dd></div><div><dt>Lesbarkeit</dt><dd data-analysis="readability">–</dd></div></dl><div class="analysis-origin"><h4>KI-Stilindikator</h4><p class="analysis-verdict"><strong id="analysis-verdict" data-level="leer">Noch keine Auswertung</strong></p><p id="analysis-origin">Aus einem fertigen Text allein lässt sich die Herkunft nicht zuverlässig bestimmen.</p><ul id="analysis-signals"><li>Nach der Bereinigung werden nachvollziehbare Stilhinweise angezeigt.</li></ul><p class="analysis-disclaimer">Hinweis statt Beweis: Aussagekräftiger ab ${MIN_STYLE_SAMPLE_WORDS} Wörtern. Menschliche und KI-generierte Texte können dieselben Merkmale besitzen.</p></div></div></details>
      </section>
    </div>
    <p class="status-message" id="status-message" role="status" aria-live="polite"></p>
    <p class="error-message" id="error-message" role="alert" aria-live="assertive"></p>
  </section>`;
}

function headerTemplate(): string {
  return `<header class="site-header"><div class="header-inner">
    <a class="brand" href="/" aria-label="Kopivaro Startseite"><span class="brand-mark" aria-hidden="true">K</span><span>Kopivaro</span></a>
    <nav class="main-nav" aria-label="Hauptnavigation"><a href="/#werkzeug">Werkzeug</a><a href="/ki-text-erkennen/">Textanalyse</a><a href="/#ratgeber">Ratgeber</a><a href="/#faq">FAQ</a></nav>
    <button class="theme-button" type="button" id="theme-button" aria-label="Farbschema wechseln"><span aria-hidden="true">◐</span><span class="theme-label">Darstellung</span></button>
  </div></header>`;
}

function footerTemplate(): string {
  return `<footer class="site-footer"><div class="footer-inner"><div><a class="brand brand--footer" href="/"><span class="brand-mark" aria-hidden="true">K</span><span>Kopivaro</span></a><p>Saubere Texte, lokal in deinem Browser.</p></div><nav aria-label="Rechtliches"><a href="/impressum/">Impressum</a><a href="/datenschutz/">Datenschutz</a><button type="button" class="footer-privacy-button" id="privacy-settings-button" hidden>Datenschutz- und Cookie-Einstellungen</button></nav><p class="footer-note"><span>Ein Dienst von <a href="${siteConfig.publisherWebsite}" rel="external">${siteConfig.publisherWebsiteName}</a></span><span>Keine Anmeldung · Kein Text-Upload · Werbung mit Google-CMP</span></p></div></footer>`;
}

function legalTemplate(page: PageKey): string {
  const operatorAddress = siteConfig.operator.addressLines.join('<br>');
  if (page === 'imprint') {
    return `<main class="legal-page"><div class="breadcrumb"><a href="/">Startseite</a><span aria-hidden="true">/</span><span>Impressum</span></div><p class="eyebrow">Rechtliche Angaben</p><h1>Impressum</h1><p class="legal-lead">Kopivaro wird von ${siteConfig.publisher} veröffentlicht.</p><section><h2>Angaben gemäß § 5 Digitale-Dienste-Gesetz (DDG)</h2><address><strong>${siteConfig.operator.name}</strong><br>Vertreten durch: ${siteConfig.operator.representative}<br>${operatorAddress}</address><h2>Kontakt</h2><p>Telefon: <a href="tel:+4915679647766">${siteConfig.operator.phone}</a><br>E-Mail: <a href="mailto:${siteConfig.contactEmail}">${siteConfig.contactEmail}</a></p><h2>Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV</h2><address>${siteConfig.operator.contentResponsible}<br>${operatorAddress}</address><h2>Geltungsbereich</h2><p>Dieses Impressum gilt für die von ${siteConfig.publisher} veröffentlichte Website <a href="${siteConfig.domain}">${siteConfig.domain}</a>.</p></section><p class="legal-footnote">Die Betreiberangaben wurden an das veröffentlichte Impressum von GermanHost angelehnt. Änderungen der Unternehmens- oder Kontaktdaten müssen auch hier zeitnah nachvollzogen werden.</p></main>`;
  }
  return `<main class="legal-page"><div class="breadcrumb"><a href="/">Startseite</a><span aria-hidden="true">/</span><span>Datenschutz</span></div><p class="eyebrow">Transparent erklärt</p><h1>Datenschutzerklärung</h1><div class="legal-notice"><strong>Vor Veröffentlichung fachlich prüfen</strong><p>Diese Beschreibung bildet die technische Umsetzung ab. Hostingprotokolle, Rechtsgrundlagen, Aufbewahrungsfristen und Betroffenenrechte müssen anhand des tatsächlichen Betriebs fachlich geprüft werden.</p></div><section><h2>1. Verantwortliche Stelle</h2><address><strong>${siteConfig.operator.name}</strong><br>Vertreten durch: ${siteConfig.operator.representative}<br>${operatorAddress}<br>Telefon: <a href="tel:+4915679647766">${siteConfig.operator.phone}</a><br>E-Mail: <a href="mailto:${siteConfig.contactEmail}">${siteConfig.contactEmail}</a></address><h2>2. Lokale Text- und Dateiverarbeitung</h2><p>Eingefügte Texte sowie lokal geöffnete PDF-, DOCX-, HTML-, Markdown- und TXT-Dateien werden ausschließlich durch JavaScript im Arbeitsspeicher deines Browsers verarbeitet. Auch Textanalyse und Datei-Export finden lokal statt. Der Inhalt wird nicht an Kopivaro, Google AdSense, eine KI-API oder den Zähler übertragen. Es gibt keine Benutzerkonten und keine serverseitige Textverarbeitung.</p><h2>3. Keine Speicherung eingefügter Inhalte</h2><p>Der eingefügte oder aus einer Datei ausgelesene Inhalt wird weder in localStorage noch in IndexedDB gespeichert. Nach dem Neuladen oder Schließen der Seite steht er nicht mehr zur Verfügung.</p><h2>4. Lokale Einstellungen und Zählermarker</h2><p>localStorage speichert das Farbschema, den zuletzt gewählten Ausgabemodus und aktivierte Bereinigungsoptionen. Zusätzlich werden zwei einfache Marker gespeichert: ob dieser Browser bereits einmal gezählt wurde und an welchem Kalendertag zuletzt ein Besuch gezählt wurde. Die Marker enthalten weder eine zufällige Kennung noch Textinhalte und werden nicht an den Zählerserver übertragen. Die Einwilligungsentscheidung der Google-CMP kann ebenfalls im Browser gespeichert werden.</p><h2>5. Öffentlicher Nutzungszähler</h2><p>Beim Seitenaufruf übermittelt der Browser ausschließlich, ob die aggregierten Werte „Besucher heute“ und „Besucher gesamt“ erhöht werden sollen. Beim ausdrücklichen Betätigen von „Text jetzt bereinigen“ wird ein Formatierungsereignis gezählt. Übertragen werden weder der eingefügte Text noch das gewählte Format oder eine Gerätekennung. Die Anwendung speichert serverseitig nur das aktuelle Datum und vier aggregierte Ganzzahlen. Die Besucherwerte sind Näherungswerte pro Browser; das Löschen des Browserspeichers kann zu einer erneuten Zählung führen. Die rechtliche Einordnung des Zugriffs auf localStorage ist vor Veröffentlichung zu prüfen.</p><h2>6. Google AdSense und Google-CMP</h2><p>Der Produktions-Build bindet Google AdSense des Anbieters Google Ireland Limited ein. Der AdSense-Code stellt außerdem die in AdSense konfigurierte Google-Consent-Management-Plattform („Privacy &amp; messaging“) bereit. Abhängig von Region und deiner Auswahl können Google und weitere in der Einwilligungsoberfläche genannte Werbepartner Cookies oder ähnliche Speichertechniken verwenden sowie technische Daten wie IP-Adresse, Browser- und Geräteinformationen, aufgerufene Seite und Anzeigeninteraktionen verarbeiten. Google und andere Drittanbieter können Cookies verwenden, um Anzeigen anhand früherer Besuche auf dieser oder anderen Websites bereitzustellen. Nicht notwendige Speicherzugriffe und personalisierte Werbung dürfen nur im Rahmen der von dir getroffenen Auswahl erfolgen.</p><p>Weitere Informationen: <a href="https://policies.google.com/technologies/partner-sites?hl=de" rel="noopener noreferrer">Datennutzung durch Google auf Websites von Partnern</a>, <a href="https://policies.google.com/privacy?hl=de" rel="noopener noreferrer">Datenschutzerklärung von Google</a> und <a href="https://adssettings.google.com/" rel="noopener noreferrer">Einstellungen für personalisierte Werbung</a>. Über „Datenschutz- und Cookie-Einstellungen“ im Footer kann die Auswahl erneut geöffnet und geändert werden, sobald die Google-CMP verfügbar ist.</p><h2>7. Veröffentlichung und technische Zugriffe</h2><p>${siteConfig.publisher} veröffentlicht diese Website. Beim Abruf der Website, des AdSense-Codes und der Zählerschnittstelle verarbeiten die jeweils eingesetzten Infrastrukturen technisch notwendige Zugriffsdaten. Der Kopivaro-Zähler selbst schreibt keine IP-Adressen oder Gerätekennungen in seine Zählerdatei. Umfang, Rechtsgrundlage und Speicherdauer von Hostingprotokollen müssen anhand der tatsächlichen Hostingkonfiguration abschließend dokumentiert werden.</p><h2>8. Kontakt und Rechte</h2><p>Für datenschutzbezogene Anfragen nutze bitte die oben genannte Kontaktadresse. Die konkreten Hinweise zu Auskunft, Berichtigung, Löschung, Einschränkung, Widerspruch, Datenübertragbarkeit, Widerruf einer Einwilligung und Beschwerde bei einer Aufsichtsbehörde sind vor Veröffentlichung anhand des tatsächlichen Betriebs fachlich zu ergänzen.</p></section></main>`;
}

interface GoogleFcApi {
  callbackQueue: unknown[];
  showRevocationMessage?: () => void;
}

function bindPrivacySettings(root: HTMLElement): void {
  const button = root.querySelector<HTMLButtonElement>('#privacy-settings-button');
  if (!button || !siteConfig.ads.enabled) return;
  const googlefc = (window as typeof window & { googlefc?: GoogleFcApi }).googlefc ?? { callbackQueue: [] };
  (window as typeof window & { googlefc?: GoogleFcApi }).googlefc = googlefc;
  googlefc.callbackQueue.push({
    CONSENT_API_READY: () => {
      button.hidden = false;
    },
  });
  button.addEventListener('click', () => {
    const api = (window as typeof window & { googlefc?: GoogleFcApi }).googlefc;
    if (api?.showRevocationMessage) api.showRevocationMessage();
  });
}

function notFoundTemplate(): string {
  return `<main class="not-found"><p class="eyebrow">Fehler 404</p><h1>Diese Seite gibt es hier nicht.</h1><p>Vielleicht wurde die Adresse geändert oder beim Kopieren ist ein Zeichen verloren gegangen.</p><a class="button button--primary" href="/">Zum vollständigen Werkzeug</a></main>`;
}

function pageTemplate(page: PageContent): string {
  const landing = page.key !== 'home';
  return `<main><section class="hero ${landing ? 'hero--landing' : ''}"><div class="hero-copy">${landing ? `<nav class="breadcrumb" aria-label="Brotkrümelnavigation"><a href="/">Startseite</a><span aria-hidden="true">/</span><span>${escapeHtml(page.eyebrow)}</span></nav>` : ''}<p class="eyebrow">${escapeHtml(page.eyebrow)}</p><h1>${escapeHtml(page.title)}</h1><p class="hero-intro">${escapeHtml(page.intro)}</p><div class="trust-row" aria-label="Produktvorteile"><span>Ohne Anmeldung</span><span>Kein Text-Upload</span><span>Kostenlos nutzbar</span></div>${landing ? '<a class="text-link" href="/">Alle Ausgabemodi im vollständigen Werkzeug ansehen →</a>' : ''}</div><aside class="hero-card" aria-label="Funktionsübersicht"><div class="mini-window"><span></span><span></span><span></span></div><p><strong>Aus chaotisch wird klar.</strong></p><div class="demo-lines demo-lines--messy"><i></i><i></i><i></i></div><div class="transform-arrow" aria-hidden="true">↓</div><div class="demo-lines demo-lines--clean"><i></i><i></i><i></i></div><small>Alles bleibt auf deinem Gerät.</small></aside></section>
    ${adSlot('introduction')}
    ${counterTemplate()}
    ${toolTemplate(page)}
    ${adSlot('result')}
    <div id="ratgeber">${renderGuide(page)}</div>
    ${adSlot('guide')}
    <section class="content-section faq-section" id="faq" aria-labelledby="faq-title"><div class="section-heading"><p class="eyebrow">Häufige Fragen</p><h2 id="faq-title">Kurz und klar beantwortet</h2></div><div class="faq-list">${renderFaq(page.faqs)}</div></section>
  </main>`;
}

function isOutputMode(value: string | undefined): value is OutputMode {
  return value !== undefined && value in modeLabels;
}

export function mountApp(root: HTMLElement): void {
  const pageKey = (root.dataset.page ?? 'home') as PageKey;
  document.documentElement.classList.add('js');
  const settings = readSettings();
  const savedTheme = settings.theme;
  const theme = savedTheme ?? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
  document.documentElement.dataset.theme = theme;

  if (pageKey === 'imprint' || pageKey === 'privacy') {
    root.innerHTML = `${headerTemplate()}${legalTemplate(pageKey)}${footerTemplate()}`;
    if (pageKey === 'privacy') root.querySelector('.legal-notice')?.remove();
    bindTheme(root, settings, theme);
    bindPrivacySettings(root);
    return;
  }
  if (pageKey === '404') {
    root.innerHTML = `${headerTemplate()}${notFoundTemplate()}${footerTemplate()}`;
    bindTheme(root, settings, theme);
    bindPrivacySettings(root);
    return;
  }

  const page = pages[pageKey in pages ? pageKey as keyof typeof pages : 'home'];
  root.innerHTML = `${headerTemplate()}${pageTemplate(page)}${footerTemplate()}`;
  bindTheme(root, settings, theme);
  bindPrivacySettings(root);
  bindPublicCounters(root);
  bindTool(root, page, settings);
}

function renderPublicCounters(root: HTMLElement, counters: PublicCounters): void {
  const formatter = new Intl.NumberFormat('de-DE');
  (Object.entries(counters) as Array<[keyof PublicCounters, number]>).forEach(([key, value]) => {
    const element = root.querySelector<HTMLElement>(`[data-public-counter="${key}"]`);
    if (element) element.textContent = formatter.format(value);
  });
}

function bindPublicCounters(root: HTMLElement): void {
  const note = root.querySelector<HTMLElement>('#counter-note');
  const loadCounters = () => {
    void registerVisit()
      .then((counters) => renderPublicCounters(root, counters))
      .catch(() => {
        if (note) note.textContent = 'Der öffentliche Zähler ist momentan nicht erreichbar; das Werkzeug funktioniert weiterhin lokal.';
      });
  };
  const schedule = () => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(loadCounters, { timeout: 1_500 });
    else globalThis.setTimeout(loadCounters, 350);
  };
  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
}

function bindTheme(root: HTMLElement, settings: StoredSettings, initialTheme: 'light' | 'dark'): void {
  let theme = initialTheme;
  root.querySelector<HTMLButtonElement>('#theme-button')?.addEventListener('click', () => {
    theme = theme === 'light' ? 'dark' : 'light';
    document.documentElement.dataset.theme = theme;
    saveSettings({ ...settings, theme });
    const status = root.querySelector<HTMLElement>('#status-message');
    if (status) status.textContent = `${theme === 'dark' ? 'Dunkle' : 'Helle'} Darstellung aktiviert.`;
  });
}

async function renderTextAnalysis(root: HTMLElement, text = ''): Promise<void> {
  const summary = root.querySelector<HTMLElement>('#analysis-summary');
  const verdict = root.querySelector<HTMLElement>('#analysis-verdict');
  const origin = root.querySelector<HTMLElement>('#analysis-origin');
  const signals = root.querySelector<HTMLUListElement>('#analysis-signals');
  if (!summary || !verdict || !origin || !signals) return;
  if (!text.trim()) {
    summary.textContent = 'Nach der Bereinigung verfügbar';
    verdict.textContent = 'Noch keine Auswertung';
    verdict.dataset.level = 'leer';
    origin.textContent = 'Aus einem fertigen Text allein lässt sich die Herkunft nicht zuverlässig bestimmen.';
    signals.replaceChildren(Object.assign(document.createElement('li'), { textContent: 'Nach der Bereinigung werden nachvollziehbare Stilhinweise angezeigt.' }));
    root.querySelectorAll<HTMLElement>('[data-analysis]').forEach((element) => { element.textContent = '–'; });
    return;
  }
  const { analyzeText } = await import('../core/text-analysis');
  const analysis = analyzeText(text);
  const formatter = new Intl.NumberFormat('de-DE');
  const values: Record<string, string> = {
    words: formatter.format(analysis.words),
    characters: formatter.format(analysis.characters),
    sentences: formatter.format(analysis.sentences),
    paragraphs: formatter.format(analysis.paragraphs),
    readingMinutes: `${analysis.readingMinutes} Min.`,
    readability: analysis.readability[0]?.toLocaleUpperCase('de-DE') + analysis.readability.slice(1),
  };
  root.querySelectorAll<HTMLElement>('[data-analysis]').forEach((element) => {
    element.textContent = values[element.dataset.analysis ?? ''] ?? '–';
  });
  const indicatorLabels: Record<AiStyleIndicator, string> = {
    'zu-kurz': 'Textprobe zu kurz',
    unauffällig: 'Unauffällige Stilprobe',
    gemischt: 'Gemischte Stilhinweise',
    auffällig: 'Auffällige KI-Stilmuster',
  };
  const indicatorLabel = indicatorLabels[analysis.aiStyleIndicator];
  verdict.textContent = indicatorLabel;
  verdict.dataset.level = analysis.aiStyleIndicator;
  summary.textContent = `${formatter.format(analysis.words)} Wörter · ${indicatorLabel}`;
  if (analysis.aiStyleIndicator === 'zu-kurz') {
    origin.textContent = `Die Herkunft bleibt offen. Für eine abgestufte Stilanalyse werden mindestens ${MIN_STYLE_SAMPLE_WORDS} Wörter benötigt; einzelne gefundene Muster stehen unten.`;
  } else if (analysis.aiStyleIndicator === 'unauffällig') {
    origin.textContent = 'Die Herkunft bleibt offen. Die messbaren Muster liefern in dieser Textprobe nur wenige Hinweise auf einen typischen KI-Schreibstil.';
  } else if (analysis.aiStyleIndicator === 'gemischt') {
    origin.textContent = `Die Herkunft bleibt offen. ${analysis.styleSignals.length} messbare Merkmale passen teilweise zu typischen KI-Textmustern, reichen aber nicht für eine auffällige Hinweisstufe.`;
  } else {
    origin.textContent = `Die Herkunft bleibt offen. ${analysis.styleSignals.length} voneinander verschiedene Merkmale ergeben eine auffällige KI-Stilprobe. Das ist ein Prüfhinweis, kein Urheberschaftsbeweis.`;
  }
  signals.replaceChildren(...(analysis.styleSignals.length > 0
    ? analysis.styleSignals.map((signal) => Object.assign(document.createElement('li'), { textContent: `${signal.label}: ${signal.detail}` }))
    : [Object.assign(document.createElement('li'), { textContent: analysis.aiStyleIndicator === 'zu-kurz' ? `Noch keine belastbaren Stilhinweise; füge mindestens ${MIN_STYLE_SAMPLE_WORDS} Wörter ein.` : 'Keine auffällige Floskeldichte, Rhythmus-Gleichförmigkeit oder Wiederholungsstruktur erkannt.' })]));
}

function bindTool(root: HTMLElement, page: PageContent, settings: StoredSettings): void {
  const editor = root.querySelector<HTMLElement>('#source-editor');
  const preview = root.querySelector<HTMLElement>('#preview');
  const sourceSelect = root.querySelector<HTMLSelectElement>('#source-format');
  const detectionNote = root.querySelector<HTMLElement>('#detection-note');
  const changeList = root.querySelector<HTMLUListElement>('#change-list');
  const status = root.querySelector<HTMLElement>('#status-message');
  const error = root.querySelector<HTMLElement>('#error-message');
  const copyButton = root.querySelector<HTMLButtonElement>('#copy-button');
  const copyPlainButton = root.querySelector<HTMLButtonElement>('#copy-plain-button');
  const downloadButton = root.querySelector<HTMLButtonElement>('#download-button');
  const downloadSelect = root.querySelector<HTMLSelectElement>('#download-format');
  const fileInput = root.querySelector<HTMLInputElement>('#file-input');
  if (!editor || !preview || !sourceSelect || !detectionNote || !changeList || !status || !error || !copyButton || !copyPlainButton || !downloadButton || !downloadSelect || !fileInput) return;

  let mode: OutputMode = page.key === 'home' && isOutputMode(settings.mode) ? settings.mode : page.mode;
  let options: CleaningOptions = page.key === 'home' ? { ...defaultCleaningOptions, ...settings.options } : { ...page.options };
  let snapshot: InputSnapshot = { plainText: '', rawHtml: '', sourceOverride: page.source };
  let result: TransformResult | null = null;
  let currentView: 'before' | 'after' = 'after';
  let timer: number | undefined;
  let preserveRawPaste = false;
  let processSequence = 0;
  const exampleText = page.key === 'analysis' || page.key === 'humanize' ? AI_STYLE_EXAMPLE_TEXT : EXAMPLE_TEXT;

  sourceSelect.value = page.source;
  root.querySelectorAll<HTMLInputElement>('[data-option]').forEach((input) => {
    const key = input.dataset.option as keyof CleaningOptions;
    input.checked = options[key];
  });

  const updateModeUi = () => {
    root.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.mode === mode));
    });
    downloadSelect.querySelector('option[value="docx"]')?.remove();
    if (mode === 'word' || mode === 'humanize') {
      const option = document.createElement('option');
      option.value = 'docx';
      option.textContent = 'DOCX';
      downloadSelect.append(option);
    }
  };

  const updatePreview = async () => {
    if (!result || !snapshot.plainText.trim()) {
      preview.className = 'preview preview--empty';
      preview.replaceChildren(Object.assign(document.createElement('p'), { textContent: 'Dein bereinigter Text erscheint hier.' }));
      return;
    }
    preview.className = `preview preview--${mode}`;
    if (currentView === 'before') {
      const pre = document.createElement('pre');
      pre.textContent = snapshot.plainText;
      preview.replaceChildren(pre);
    } else {
      const { setSanitizedHtml } = await import('../core/sanitize');
      setSanitizedHtml(preview, result.html);
    }
  };

  const process = async (announce = false) => {
    const sequence = ++processSequence;
    window.clearTimeout(timer);
    error.textContent = '';
    snapshot.sourceOverride = sourceSelect.value as SourceFormat;
    if (!snapshot.plainText.trim()) {
      result = null;
      detectionNote.textContent = 'Noch kein Text erkannt';
      changeList.replaceChildren(Object.assign(document.createElement('li'), { textContent: 'Noch keine Verarbeitung durchgeführt.' }));
      copyButton.disabled = true;
      copyPlainButton.disabled = true;
      downloadButton.disabled = true;
      await updatePreview();
      await renderTextAnalysis(root);
      if (announce) status.textContent = 'Bitte füge zuerst einen Text ein.';
      return;
    }
    try {
      const input = { ...snapshot };
      const selectedMode = mode;
      const selectedOptions = { ...options };
      const { transform } = await import('../core/pipeline');
      if (sequence !== processSequence) return;
      result = transform(input, selectedMode, selectedOptions);
      detectionNote.textContent = `Vermutlich erkannt: ${result.detected.label}`;
      const items = formatStats(result.stats);
      changeList.replaceChildren(...(items.length > 0 ? items : ['Der Text war bereits sauber formatiert.']).map((label) => Object.assign(document.createElement('li'), { textContent: label })));
      copyButton.disabled = false;
      copyPlainButton.disabled = false;
      downloadButton.disabled = false;
      await updatePreview();
      await renderTextAnalysis(root, result.text);
      if (announce) status.textContent = totalChanges(result.stats) > 0
        ? mode === 'humanize' ? 'Text wurde natürlicher formuliert. Prüfe jetzt Inhalt und Ton.' : 'Text wurde bereinigt. Prüfe jetzt die Vorschau.'
        : mode === 'humanize' ? 'Keine klaren, sicher ersetzbaren Stilmuster gefunden. Der Text wirkt bereits direkt oder benötigt eine redaktionelle Überarbeitung.' : 'Prüfung abgeschlossen: Der Text war bereits sauber.';
    } catch {
      result = null;
      await renderTextAnalysis(root);
      error.textContent = 'Der Inhalt konnte nicht verarbeitet werden. Entferne ungewöhnliche Sonderzeichen oder setze das Werkzeug zurück.';
    }
  };

  const scheduleProcess = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => { void process(false); }, snapshot.plainText.length > 100_000 ? 650 : 280);
  };

  editor.addEventListener('paste', async (event) => {
    event.preventDefault();
    const plain = event.clipboardData?.getData('text/plain') ?? '';
    const rawHtml = event.clipboardData?.getData('text/html') ?? '';
    const { sanitizeHtml } = await import('../core/sanitize');
    const safeForEditor = rawHtml ? sanitizeHtml(rawHtml) : escapeHtml(plain).replace(/\n/g, '<br>');
    preserveRawPaste = true;
    document.execCommand('insertHTML', false, safeForEditor);
    snapshot.plainText = editor.innerText;
    snapshot.rawHtml = rawHtml || editor.innerHTML;
    window.setTimeout(() => { preserveRawPaste = false; }, 0);
    scheduleProcess();
  });

  editor.addEventListener('input', () => {
    snapshot.plainText = editor.innerText;
    if (!preserveRawPaste) snapshot.rawHtml = editor.innerHTML;
    scheduleProcess();
  });

  root.querySelector<HTMLButtonElement>('#file-button')?.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    error.textContent = '';
    status.textContent = `${file.name} wird ausschließlich lokal geöffnet …`;
    try {
      const [{ importLocalFile }, { setSanitizedHtml }] = await Promise.all([
        import('../imports/file-import'),
        import('../core/sanitize'),
      ]);
      const imported = await importLocalFile(file);
      if (imported.rawHtml) setSanitizedHtml(editor, imported.rawHtml);
      else editor.textContent = imported.plainText;
      snapshot = { plainText: imported.plainText, rawHtml: imported.rawHtml, sourceOverride: imported.source };
      sourceSelect.value = imported.source;
      await process(false);
      status.textContent = `${imported.label} wurde lokal geöffnet. Prüfe jetzt das Ergebnis.`;
      editor.focus();
    } catch (fileError) {
      error.textContent = fileError instanceof Error ? fileError.message : 'Die Datei konnte nicht lokal geöffnet werden.';
      status.textContent = '';
    } finally {
      fileInput.value = '';
    }
  });

  root.querySelector<HTMLButtonElement>('#example-button')?.addEventListener('click', async () => {
    editor.textContent = exampleText;
    snapshot = { plainText: exampleText, rawHtml: '', sourceOverride: 'auto' };
    sourceSelect.value = 'auto';
    await process(true);
    editor.focus();
  });

  root.querySelector<HTMLButtonElement>('#reset-button')?.addEventListener('click', async () => {
    window.clearTimeout(timer);
    editor.replaceChildren();
    snapshot = { plainText: '', rawHtml: '', sourceOverride: page.source };
    sourceSelect.value = page.source;
    result = null;
    currentView = 'after';
    root.querySelectorAll<HTMLButtonElement>('[data-view]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.view === 'after')));
    status.textContent = 'Eingabe und Ergebnis wurden zurückgesetzt.';
    error.textContent = '';
    await process(false);
    editor.focus();
  });

  root.querySelector<HTMLButtonElement>('#clean-button')?.addEventListener('click', async () => {
    await process(true);
    if (!result || !snapshot.plainText.trim()) return;
    void registerFormatting()
      .then((counters) => renderPublicCounters(root, counters))
      .catch(() => {
        const note = root.querySelector<HTMLElement>('#counter-note');
        if (note) note.textContent = 'Die Formatierung war erfolgreich; nur der öffentliche Zähler konnte nicht aktualisiert werden.';
      });
  });
  sourceSelect.addEventListener('change', () => { void process(false); });

  root.querySelectorAll<HTMLInputElement>('[data-option]').forEach((input) => input.addEventListener('change', () => {
    const key = input.dataset.option as keyof CleaningOptions;
    options = { ...options, [key]: input.checked };
    saveSettings({ ...settings, mode, options });
    void process(false);
  }));

  root.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((button) => button.addEventListener('click', () => {
    const selected = button.dataset.mode;
    if (!isOutputMode(selected)) return;
    mode = selected;
    saveSettings({ ...settings, mode, options });
    updateModeUi();
    void process(false);
    status.textContent = `${modeLabels[mode].title} ausgewählt.`;
  }));

  root.querySelectorAll<HTMLButtonElement>('[data-view]').forEach((button) => button.addEventListener('click', () => {
    currentView = button.dataset.view === 'before' ? 'before' : 'after';
    root.querySelectorAll<HTMLButtonElement>('[data-view]').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
    void updatePreview();
  }));

  copyButton.addEventListener('click', async () => {
    if (!result) return;
    try {
      const { copyFormatted, copyPlain } = await import('../exports/clipboard');
      if (mode === 'word' || mode === 'email' || mode === 'html' || mode === 'humanize') await copyFormatted(result.canonicalHtml, result.text);
      else await copyPlain(result.text);
      status.textContent = 'Ausgabe wurde in die Zwischenablage kopiert.';
    } catch (copyError) {
      error.textContent = copyError instanceof Error ? copyError.message : 'Kopieren war in diesem Browser nicht möglich.';
    }
  });

  copyPlainButton.addEventListener('click', async () => {
    if (!result) return;
    try {
      const { copyPlain } = await import('../exports/clipboard');
      await copyPlain(result.text);
      status.textContent = mode === 'html' ? 'HTML-Quelltext wurde kopiert.' : 'Klartext wurde kopiert.';
    } catch (copyError) {
      error.textContent = copyError instanceof Error ? copyError.message : 'Kopieren war in diesem Browser nicht möglich.';
    }
  });

  downloadButton.addEventListener('click', async () => {
    if (!result) return;
    downloadButton.disabled = true;
    try {
      const { triggerDownload } = await import('../exports/download');
      const filename = await triggerDownload(downloadSelect.value as DownloadFormat, result.canonicalHtml);
      status.textContent = `${filename} wurde erstellt.`;
    } catch {
      error.textContent = 'Die Datei konnte nicht erzeugt werden. Wähle ein anderes Format oder versuche es erneut.';
    } finally {
      downloadButton.disabled = false;
    }
  });

  updateModeUi();
  void updatePreview();
}
